/**
 * Text creation flow, step 3: name the text, choose where it lives, confirm what
 * is created (handoff §6, §16, §19.4).
 *
 * The daily word budget is the server's (POST /texts/flow/budget): this page
 * shows what the save would do with the card marks right now and never invents a
 * number. Saving the text never depends on the budget; words that do not fit wait
 * (ADR-115 Amendment 2). When the text is off and some words cannot become cards
 * today, the page says which, before saving, and offers to keep the text so they
 * can wait.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ChevronRight, Clock } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { useLocale } from '../i18n/context'
import { useIsDesktop } from '../hooks/useIsDesktop'
import { useSaveTextFlow, useTextBudgetCheck, useTextLimits } from '../hooks/useTexts'
import { useTextFlowStore } from '../stores/textFlowStore'
import { useSaveToast } from '../components/texts/useSaveToast'
import type { TextBudgetCheck, TextDestinationKind, TextFlowPayload } from '../types'
import { gapCount, saveCardMarks, saveForms } from '../utils/textMarks'
import {
  budgetView,
  defaultSaveText,
  defaultTitle,
  destinationRules,
  effectiveDestination,
  saveButton,
} from '../utils/textFlow'
import { apiErrorCode, apiErrorDetail } from '../utils/textFlowErrors'
import FlowFrame from '../components/texts/FlowFrame'
import DeckPickerSheet from '../components/texts/DeckPickerSheet'
import { useRefreshWhen } from '../components/texts/useRefreshWhen'
import { Eyebrow, GhostButton, InfoPanel, PrimaryButton, Radio, SecondaryPill, Switch, WordChip } from '../components/texts/ui'

const SAMPLE_WORD = 'abc' // i18n-exempt: the sample glyphs of handoff §6.1, not a word of the interface

export default function TextFlowSavePage() {
  const { tr } = useLocale()
  const tf = tr.textFlow
  const t = tf.save
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const flow = useTextFlowStore()
  const toastSave = useSaveToast()
  const leaving = useRef(false)
  const { analysis } = flow
  const limits = useTextLimits().data
  const check = useTextBudgetCheck()
  const save = useSaveTextFlow()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [checked, setChecked] = useState<{ key: string; data: TextBudgetCheck } | null>(null)
  const when = useRefreshWhen()

  const words = analysis?.words
  const marks = useMemo(() => (analysis ? saveCardMarks(analysis, flow.marks) : []), [analysis, flow.marks])
  // The gap forms the server will search; those it found nowhere are left out (step 2 says which).
  const forms = useMemo(
    () => (words ? saveForms(flow.body, words, flow.marks, flow.gapMatch) : []),
    [flow.body, words, flow.marks, flow.gapMatch],
  )
  const gaps = words ? gapCount(flow.body, words, flow.marks, flow.gapMatch) : 0
  const cardCount = marks.length
  const entry = flow.entry ?? 'essay'

  const rules = destinationRules(cardCount)
  const kind: TextDestinationKind = effectiveDestination(flow.destination, cardCount, !!flow.deck)
  // Gaps live in the text: with one, the text is always saved (§6.1).
  const saveText = cardCount === 0 || gaps > 0 ? true : (flow.saveText ?? defaultSaveText(entry))
  const titleValue = flow.title ?? defaultTitle(flow.body)

  const payload: TextFlowPayload = useMemo(() => ({
    title: titleValue.trim() || defaultTitle(flow.body) || '…',
    body: flow.body,
    forms,
    card_marks: marks,
    destination: {
      kind,
      deck_id: kind === 'existing_deck' ? flow.deck?.id ?? null : null,
      is_active_for_learning: flow.isActive,
    },
    save_text: saveText,
  }), [titleValue, flow.body, forms, marks, kind, flow.deck, flow.isActive, saveText])

  // ── The server's budget check for what is marked now ──────────────────────
  const checkKey = JSON.stringify([marks, kind, flow.deck?.id ?? null, saveText])
  const needsCheck = cardCount > 0 && (kind !== 'existing_deck' || !!flow.deck)
  useEffect(() => {
    if (!needsCheck) return
    const handle = window.setTimeout(() => {
      check.mutate(payload, { onSuccess: (data) => setChecked({ key: checkKey, data }) })
    }, 200)
    return () => window.clearTimeout(handle)
    // The title does not change what the budget says; the rest of the payload is in the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkKey, needsCheck])

  const current = needsCheck && checked?.key === checkKey ? checked.data : null
  const view = budgetView(current, saveText, cardCount)
  const button = saveButton(view, saveText, cardCount)

  // Once a cards-only save has left, the cleared flow must not send this page back to step 1.
  if (!analysis) return leaving.current ? null : <Navigate to="/texts/new" replace />

  const title = entry === 'words' ? tf.entryWords : tf.entryEssay
  const back = () => navigate('/texts/new/words')

  // ── Save ──────────────────────────────────────────────────────────────────
  function submit() {
    if (button.disabled || save.isPending) return
    save.mutate(payload, {
      onSuccess: (result) => {
        // `replace` keeps the save step out of the history, so back never returns to it.
        if (result.text) {
          // The text screen shows the toast and the generation run, and clears the
          // flow's state once it is mounted: clearing it here would send this page
          // back to step 1 before the navigation lands.
          navigate(`/texts/${result.text.id}`, {
            replace: true,
            state: { saved: result, from: flow.origin, fromLabel: flow.originLabel ?? undefined },
          })
          return
        }
        // A cards-only save keeps no text, so there is no text screen to land on: the
        // cards are in the deck, and the toast says what was made.
        leaving.current = true
        toastSave(result)
        navigate(result.deck_id ? `/decks/${result.deck_id}` : flow.origin, { replace: true })
        flow.reset()
      },
      onError: (err) => {
        const code = apiErrorCode(err)
        const detail = apiErrorDetail(err)
        if (code === 'TEXT_TOO_LONG') toast.error(t.tooLong(Number(detail?.max_chars ?? limits?.max_chars ?? 0)))
        else if (code === 'TEXT_TOO_MANY_GAPS') toast.error(t.tooManyGaps(Number(detail?.max_gaps ?? limits?.max_gaps ?? 0)))
        else toast.error(t.saveFailed)
      },
    })
  }

  const buttonLabel =
    save.isPending ? t.saving
    : button.label.kind === 'textOnly' ? t.buttonTextOnly
    : button.label.kind === 'cardsOnly' ? t.buttonCardsOnly(button.label.n)
    : t.buttonTextAndCards(button.label.n)

  // ── Pieces ────────────────────────────────────────────────────────────────
  const waitingChips = (words: { word: string; lemma: string }[]) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingLeft: 0 }} data-testid="waiting-chips">
      {words.map((w) => <WordChip key={w.lemma}>{w.word}</WordChip>)}
    </div>
  )

  const budgetPanel = (() => {
    if (!view || !current) return null
    const refresh = when(current.budget.refresh_at)
    if (view.kind === 'waits') {
      return (
        <InfoPanel testId="budget-panel" icon={<Clock size={18} />} title={t.budget.panelTitle(current.budget.remaining ?? view.accepted)}>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: 'var(--text-secondary)' }}>
            {t.budget.panelBody(view.waiting.length, refresh)}
          </p>
          {waitingChips(view.waiting)}
        </InfoPanel>
      )
    }
    if (view.kind === 'exhausted') {
      return (
        <InfoPanel testId="budget-exhausted" icon={<Clock size={18} />} title={t.budget.exhaustedTitle}>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: 'var(--text-secondary)' }}>
            {t.budget.exhaustedBody(view.waiting.length, refresh)}
          </p>
          {waitingChips(view.waiting)}
        </InfoPanel>
      )
    }
    if (view.kind === 'cardsOnly') {
      return (
        <InfoPanel testId="cards-only-warning" tone="notice" icon={<Clock size={18} />} title={t.budget.cardsOnlyTitle(view.waiting.length)}>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: 'var(--text-secondary)' }}>
            {t.budget.cardsOnlyBody(current.budget.remaining ?? view.accepted, refresh)}
          </p>
          {waitingChips(view.waiting)}
          <div>
            <SecondaryPill height={40} onClick={() => flow.setSaveText(true)} data-testid="turn-on-save-text">
              {t.budget.cardsOnlyAction}
            </SecondaryPill>
          </div>
        </InfoPanel>
      )
    }
    return null
  })()

  const sample = (dashed: boolean) => (
    <span
      aria-hidden
      style={{
        marginLeft: 6, padding: '0 4px', borderRadius: 4, fontFamily: 'var(--font-content)', fontSize: 14,
        color: 'var(--text-primary)',
        ...(dashed
          ? { textDecoration: 'underline dashed var(--accent) 2px', textUnderlineOffset: 6 }
          : { background: 'var(--cefr-b1-bg)' }),
      }}
    >
      {SAMPLE_WORD}
    </span>
  )

  const summary = cardCount === 0 && gaps === 0 ? (
    <div style={{ padding: '14px 16px', borderRadius: 16, background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
      <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{t.nothingTitle}</p>
      <p style={{ margin: '4px 0 0', fontSize: 13, lineHeight: 1.5, color: 'var(--text-secondary)' }}>{t.nothingBody}</p>
    </div>
  ) : (
    <div
      data-testid="save-summary"
      style={{ display: 'flex', borderRadius: 16, background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
    >
      <div style={{ flex: 1, padding: '14px 16px' }}>
        <div style={{ fontFamily: 'var(--font-content)', fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>
          {view && (view.kind === 'waits' || view.kind === 'exhausted') ? (
            <>
              <span data-testid="summary-accepted">{view.kind === 'waits' ? view.accepted : 0}</span>
              <span style={{ fontFamily: 'var(--font-ui)', fontSize: 16, fontWeight: 400, color: 'var(--text-secondary)' }}>
                {' '}{t.ofTotal(view.kind === 'waits' ? view.accepted + view.waiting.length : view.waiting.length)}
              </span>
            </>
          ) : (
            <span data-testid="summary-cards">{cardCount}</span>
          )}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {view && (view.kind === 'waits' || view.kind === 'exhausted') ? t.cardsTodayLabel : t.cardsLabel(cardCount)}
          {sample(false)}
        </div>
      </div>
      <div style={{ width: 1, background: 'var(--border)', margin: '12px 0' }} />
      <div style={{ flex: 1, padding: '14px 16px' }}>
        <div style={{ fontFamily: 'var(--font-content)', fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }} data-testid="summary-gaps">{gaps}</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {t.gapsLabel(gaps)}
          {sample(true)}
        </div>
      </div>
    </div>
  )

  const radioRow = (
    id: TextDestinationKind,
    label: string,
    desc: string | null,
    opts: { disabled?: boolean; chevron?: boolean; onSelect: () => void },
  ) => {
    const on = kind === id
    return (
      <button
        type="button"
        role="radio"
        aria-checked={on}
        aria-disabled={opts.disabled || undefined}
        data-testid={`dest-${id}`}
        onClick={opts.disabled ? undefined : opts.onSelect}
        style={{
          width: '100%', display: 'flex', alignItems: 'flex-start', gap: 12, textAlign: 'left',
          padding: isDesktop ? '12px 16px' : '14px 16px', border: 'none', borderTop: id === 'new_deck' ? 'none' : '1px solid var(--border)',
          background: on ? 'var(--accent-soft)' : 'transparent', fontFamily: 'var(--font-ui)',
          cursor: opts.disabled ? 'not-allowed' : 'pointer',
          color: opts.disabled ? 'var(--text-tertiary)' : 'var(--text-primary)',
        }}
      >
        <span style={{ marginTop: 2 }}><Radio on={on} /></span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 16, fontWeight: 600 }}>{label}</span>
          {desc && <span style={{ display: 'block', marginTop: 2, fontSize: 13, lineHeight: 1.45, color: opts.disabled ? 'var(--text-tertiary)' : 'var(--text-secondary)' }}>{desc}</span>}
        </span>
        {opts.chevron && <ChevronRight size={18} aria-hidden style={{ marginTop: 3, color: 'var(--text-secondary)' }} />}
      </button>
    )
  }

  const destination = (
    <div role="radiogroup" aria-label={t.whereLabel} style={{ borderRadius: 16, overflow: 'hidden', background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
      {radioRow('new_deck', t.newDeck.title, t.newDeck.desc, { onSelect: () => flow.setDestination('new_deck') })}
      {radioRow('existing_deck', t.existingDeck.title, flow.deck ? flow.deck.title : t.existingDeck.choose, {
        chevron: true,
        onSelect: () => setPickerOpen(true),
      })}
      {radioRow('none', t.noDeck.title, rules.noDeckAvailable ? t.noDeck.chosen : t.noDeck.blocked, {
        disabled: !rules.noDeckAvailable,
        onSelect: () => flow.setDestination('none'),
      })}
    </div>
  )

  const switchRow = (id: string, label: string, desc: string | null, el: React.ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div id={id} style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{label}</div>
        {desc && <div style={{ marginTop: 2, fontSize: 13, lineHeight: 1.45, color: 'var(--text-secondary)' }}>{desc}</div>}
      </div>
      {el}
    </div>
  )

  const settings = cardCount > 0 && (
    <div style={{ padding: 16, borderRadius: 16, background: 'var(--bg-surface)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 14 }}>
      {switchRow(
        'sw-save-text', t.saveTextSwitch.title,
        gaps > 0 ? t.saveTextSwitch.locked : saveText ? null : t.saveTextSwitch.free,
        <Switch checked={saveText} onChange={flow.setSaveText} disabled={gaps > 0} labelledBy="sw-save-text" />,
      )}
      {kind === 'new_deck' && (
        <>
          <div style={{ height: 1, background: 'var(--border)' }} />
          {switchRow(
            'sw-queue', t.queueSwitch.title, t.queueSwitch.desc,
            <Switch checked={flow.isActive} onChange={flow.setActive} labelledBy="sw-queue" />,
          )}
        </>
      )}
    </div>
  )

  const skippedNote = view && view.skipped > 0 && (
    <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: 'var(--text-secondary)' }}>{t.skipped(view.skipped)}</p>
  )

  const form = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: isDesktop ? '28px 0' : '16px 16px 24px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label htmlFor="text-title"><Eyebrow>{t.titleLabel}</Eyebrow></label>
        <input
          id="text-title"
          type="text"
          value={titleValue}
          onChange={(e) => flow.setTitle(e.target.value)}
          style={{
            height: 48, boxSizing: 'border-box', padding: '0 16px', borderRadius: 8, fontSize: 15,
            border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)',
            fontFamily: 'var(--font-ui)', width: '100%',
          }}
          onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)' }}
          onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border)' }}
        />
      </div>
      {summary}
      {budgetPanel}
      {skippedNote}
      <Eyebrow style={{ marginTop: 4 }}>{t.whereLabel}</Eyebrow>
      {destination}
      {settings}
    </div>
  )

  return (
    <>
      <FlowFrame
        step={3}
        title={title}
        leading="back"
        onLeave={back}
        backLabel={tf.stepShort(2)}
        subtitle={tf.stepSubtitle(3, tf.stepNames.save)}
        maxWidth={600}
        footer={
          isDesktop ? undefined : (
            <PrimaryButton full disabled={button.disabled || save.isPending} onClick={submit} data-testid="save-button">
              {buttonLabel}
            </PrimaryButton>
          )
        }
      >
        {form}
        {isDesktop && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, paddingBottom: 28 }}>
            <GhostButton onClick={back}>{tf.back}</GhostButton>
            <PrimaryButton height={44} disabled={button.disabled || save.isPending} onClick={submit} data-testid="save-button">
              {buttonLabel}
            </PrimaryButton>
          </div>
        )}
      </FlowFrame>
      <DeckPickerSheet
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(deck) => { flow.setDeck(deck); setPickerOpen(false) }}
      />
    </>
  )
}
